import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/context/AuthContext";
import CVPreviewModal from "./CVPreviewModal";

declare global {
  interface Window {
    html2pdf: unknown;
  }
}

interface CVBuilderTabProps {
  user: AppUser;
}

interface Experience {
  id: string;
  company: string;
  position: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  description: string;
}

interface Education {
  id: string;
  school: string;
  degree: string;
  field: string;
  graduation_year: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  year: string;
}

interface Language {
  id: string;
  name: string;
  level: string;
}

interface CVFormData {
  title: string;
  address: string;
  birth_date: string;
  gender: string;
  website: string;
  summary: string;
  experience: Experience[];
  education: Education[];
  skills: string;
  certifications: Certification[];
  languages: Language[];
}

let idCounter = 1;
function genId(): string {
  return `new_${Date.now()}_${idCounter++}`;
}

const blankExp = (): Experience => ({
  id: genId(), company: "", position: "", start_date: "", end_date: "", is_current: false, description: "",
});

const blankEdu = (): Education => ({
  id: genId(), school: "", degree: "", field: "", graduation_year: "",
});

const blankCert = (): Certification => ({
  id: genId(), name: "", issuer: "", year: "",
});

const blankLang = (): Language => ({
  id: genId(), name: "", level: "Trung cấp",
});

const defaultForm: CVFormData = {
  title: "",
  address: "",
  birth_date: "",
  gender: "",
  website: "",
  summary: "",
  experience: [],
  education: [],
  skills: "",
  certifications: [],
  languages: [],
};

const languageLevels = ["Cơ bản", "Trung cấp", "Thành thạo", "Bản ngữ"];

export default function CVBuilderTab({ user }: CVBuilderTabProps) {
  const [form, setForm] = useState<CVFormData>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const hasUnsavedRef = useRef(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from("vietnamcv_cvs")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!cancelled && !error && data) {
        setForm({
          title: data.title || "",
          address: data.address || "",
          birth_date: data.birth_date || "",
          gender: data.gender || "",
          website: data.website || "",
          summary: data.summary || "",
          experience: Array.isArray(data.experience) ? data.experience : [],
          education: Array.isArray(data.education) ? data.education : [],
          skills: Array.isArray(data.skills) ? data.skills.join(", ") : data.skills || "",
          certifications: Array.isArray(data.certifications) ? data.certifications : [],
          languages: Array.isArray(data.languages) ? data.languages : [],
        });
      } else if (!cancelled) {
        // Pre-fill from profile
        const { data: profileData } = await supabase
          .from("vietnamcv_profiles")
          .select("name, phone, address, birth_date, gender")
          .eq("id", user.id)
          .maybeSingle();

        if (profileData) {
          setForm((prev) => ({
            ...prev,
            address: profileData.address || "",
            birth_date: profileData.birth_date || "",
            gender: profileData.gender || "",
          }));
        }
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [user.id]);

  const updateField = <K extends keyof CVFormData>(key: K, value: CVFormData[K]) => {
    hasUnsavedRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateExp = (idx: number, field: keyof Experience, value: string | boolean) => {
    hasUnsavedRef.current = true;
    setForm((prev) => {
      const updated = [...prev.experience];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, experience: updated };
    });
  };

  const updateEdu = (idx: number, field: keyof Education, value: string) => {
    hasUnsavedRef.current = true;
    setForm((prev) => {
      const updated = [...prev.education];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, education: updated };
    });
  };

  const updateCert = (idx: number, field: keyof Certification, value: string) => {
    hasUnsavedRef.current = true;
    setForm((prev) => {
      const updated = [...prev.certifications];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, certifications: updated };
    });
  };

  const updateLang = (idx: number, field: keyof Language, value: string) => {
    hasUnsavedRef.current = true;
    setForm((prev) => {
      const updated = [...prev.languages];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, languages: updated };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setToast(null);

    const payload = {
      user_id: user.id,
      title: form.title.trim() || null,
      address: form.address.trim() || null,
      birth_date: form.birth_date || null,
      gender: form.gender || null,
      website: form.website.trim() || null,
      summary: form.summary.trim() || null,
      experience: form.experience.filter((e) => e.company || e.position),
      education: form.education.filter((e) => e.school || e.degree),
      skills: form.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      certifications: form.certifications.filter((c) => c.name),
      languages: form.languages.filter((l) => l.name),
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from("vietnamcv_cvs").upsert(payload);

    if (error) {
      setToast({ type: "error", message: "Lỗi khi lưu CV: " + error.message });
    } else {
      hasUnsavedRef.current = false;
      setToast({ type: "success", message: "CV đã được lưu thành công!" });
    }
    setSaving(false);
    setTimeout(() => setToast(null), 4000);
  };

  const handleDownloadPDF = async () => {
    const html2pdf = (
      typeof window !== "undefined" ? (window as Record<string, unknown>).html2pdf : null
    ) as ((element: HTMLElement, options?: Record<string, unknown>) => { save: (filename: string) => void }) | null;

    if (!html2pdf) {
      setToast({ type: "error", message: "Thư viện PDF chưa sẵn sàng. Vui lòng thử lại sau." });
      return;
    }

    setDownloading(true);
    setToast(null);

    // Build a clean CV HTML element
    const cvDiv = document.createElement("div");
    cvDiv.style.cssText = "padding:24px;font-family:Lexend,Arial,sans-serif;color:#1a1a1a;max-width:800px;margin:0 auto;background:#fff;";
    cvDiv.innerHTML = buildCVHTML();

    document.body.appendChild(cvDiv);

    try {
      await html2pdf(cvDiv, {
        margin: [0, 0, 0, 0],
        filename: `CV_${user.name.replace(/\s+/g, "_")}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }).save(`CV_${user.name.replace(/\s+/g, "_")}.pdf`);

      setToast({ type: "success", message: "Đã tải CV xuống thành công!" });
    } catch {
      setToast({ type: "error", message: "Không thể tạo PDF. Vui lòng thử lại." });
    } finally {
      document.body.removeChild(cvDiv);
      setDownloading(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const buildCVHTML = () => {
    const expList = form.experience.filter((e) => e.company || e.position);
    const eduList = form.education.filter((e) => e.school || e.degree);
    const certList = form.certifications.filter((c) => c.name);
    const langList = form.languages.filter((l) => l.name);
    const skillList = form.skills.split(",").map((s) => s.trim()).filter(Boolean);

    return `
      <div style="margin-bottom:24px;border-bottom:3px solid #0d9488;padding-bottom:16px;">
        <h1 style="font-size:24px;font-weight:700;margin:0 0 4px 0;color:#0f172a;">${user.name}</h1>
        ${form.title ? `<p style="font-size:14px;color:#0d9488;margin:0 0 8px 0;font-weight:600;">${form.title}</p>` : ""}
        <div style="font-size:12px;color:#475569;">
          ${user.email ? `<span style="margin-right:16px;">&#9993; ${user.email}</span>` : ""}
          ${user.phone ? `<span style="margin-right:16px;">&#9742; ${user.phone}</span>` : ""}
          ${form.address ? `<span style="margin-right:16px;">&#9872; ${form.address}</span>` : ""}
          ${form.website ? `<span>${form.website}</span>` : ""}
        </div>
      </div>
      ${form.summary ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Giới thiệu</h3><p style="font-size:12px;color:#334155;line-height:1.6;margin:0;">${form.summary}</p></div>` : ""}
      ${expList.length > 0 ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Kinh nghiệm làm việc</h3>${expList.map((e) => `<div style="margin-bottom:10px;"><div style="display:flex;justify-content:space-between;align-items:baseline;"><strong style="font-size:13px;color:#1e293b;">${e.position}</strong><span style="font-size:11px;color:#64748b;">${e.start_date} - ${e.is_current ? "Hiện tại" : e.end_date}</span></div><p style="font-size:12px;color:#0d9488;margin:2px 0;">${e.company}</p><p style="font-size:11px;color:#475569;margin:2px 0;line-height:1.5;">${e.description}</p></div>`).join("")}</div>` : ""}
      ${eduList.length > 0 ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Học vấn</h3>${eduList.map((e) => `<div style="margin-bottom:8px;"><strong style="font-size:13px;color:#1e293b;">${e.degree}${e.field ? ` - ${e.field}` : ""}</strong><p style="font-size:12px;color:#0d9488;margin:2px 0;">${e.school}${e.graduation_year ? ` (${e.graduation_year})` : ""}</p></div>`).join("")}</div>` : ""}
      ${skillList.length > 0 ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Kỹ năng</h3><p style="font-size:12px;color:#334155;margin:0;">${skillList.join(" &bull; ")}</p></div>` : ""}
      ${certList.length > 0 ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Chứng chỉ</h3>${certList.map((c) => `<div style="margin-bottom:4px;font-size:12px;color:#334155;"><strong>${c.name}</strong>${c.issuer ? ` - ${c.issuer}` : ""}${c.year ? ` (${c.year})` : ""}</div>`).join("")}</div>` : ""}
      ${langList.length > 0 ? `<div style="margin-bottom:16px;"><h3 style="font-size:14px;font-weight:700;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:8px;">Ngôn ngữ</h3>${langList.map((l) => `<div style="margin-bottom:4px;font-size:12px;color:#334155;"><strong>${l.name}</strong> - ${l.level}</div>`).join("")}</div>` : ""}
    `;
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-12 bg-background-100 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-background-200/70">
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground-900">
            CV của tôi
          </h3>
          <p className="text-sm text-foreground-500">
            Tạo và quản lý CV trực tuyến để ứng tuyển nhanh chóng
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-1.5 px-4 py-2 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line text-base"></i>
            </div>
            <span className="hidden sm:inline">Xem trước</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-1.5 px-4 py-2 bg-accent-600 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {downloading ? (
              <>
                <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
                <span className="hidden sm:inline">Đang tải...</span>
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-download-line text-base"></i>
                </div>
                <span className="hidden sm:inline">Tải PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${
            toast.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`}></i>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Basic Info */}
        <section>
          <h4 className="font-heading font-semibold text-sm text-foreground-800 mb-4 flex items-center gap-2">
            <i className="ri-user-line text-primary-500"></i>
            Thông tin cơ bản
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Vị trí mong muốn</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="VD: Nhân viên sản xuất"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Địa chỉ</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="VD: Quận 7, TP.HCM"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Ngày sinh</label>
              <input
                type="text"
                value={form.birth_date}
                onChange={(e) => updateField("birth_date", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="dd/mm/yyyy"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Website / Mạng xã hội</label>
              <input
                type="text"
                value={form.website}
                onChange={(e) => updateField("website", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="VD: linkedin.com/in/yourname"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-foreground-600 mb-1">
              Giới thiệu bản thân
            </label>
            <textarea
              value={form.summary}
              onChange={(e) => updateField("summary", e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              placeholder="Mô tả ngắn về bản thân, mục tiêu nghề nghiệp..."
            ></textarea>
            <p className="text-[11px] text-foreground-400 mt-1 text-right">{form.summary.length}/500</p>
          </div>
        </section>

        {/* Experience */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-sm text-foreground-800 flex items-center gap-2">
              <i className="ri-briefcase-line text-primary-500"></i>
              Kinh nghiệm làm việc
            </h4>
          </div>

          {form.experience.length === 0 && (
            <p className="text-sm text-foreground-400 text-center py-4 bg-background-50 rounded-lg">
              Chưa có kinh nghiệm làm việc. Nhấn &quot;Thêm&quot; để bổ sung.
            </p>
          )}

          <div className="space-y-4">
            {form.experience.map((exp, i) => (
              <div key={exp.id} className="p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-foreground-500">Kinh nghiệm #{i + 1}</span>
                  <button
                    onClick={() => updateField("experience", form.experience.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line"></i>
                    </div>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={exp.position}
                    onChange={(e) => updateExp(i, "position", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Chức danh"
                  />
                  <input
                    type="text"
                    value={exp.company}
                    onChange={(e) => updateExp(i, "company", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Công ty"
                  />
                  <input
                    type="text"
                    value={exp.start_date}
                    onChange={(e) => updateExp(i, "start_date", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Từ (MM/YYYY)"
                  />
                  {!exp.is_current ? (
                    <input
                      type="text"
                      value={exp.end_date}
                      onChange={(e) => updateExp(i, "end_date", e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                      placeholder="Đến (MM/YYYY)"
                    />
                  ) : (
                    <div className="flex items-center px-3 py-2 text-sm text-foreground-400">Đang làm việc</div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exp.is_current}
                      onChange={(e) => updateExp(i, "is_current", e.target.checked)}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                    />
                    <span className="text-xs text-foreground-600">Đang làm việc tại đây</span>
                  </label>
                </div>
                <textarea
                  value={exp.description}
                  onChange={(e) => updateExp(i, "description", e.target.value)}
                  rows={2}
                  className="w-full mt-3 px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all resize-none"
                  placeholder="Mô tả công việc..."
                ></textarea>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => updateField("experience", [...form.experience, blankExp()])}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Thêm
            </button>
          </div>
        </section>

        {/* Education */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-sm text-foreground-800 flex items-center gap-2">
              <i className="ri-graduation-cap-line text-primary-500"></i>
              Học vấn
            </h4>
          </div>

          {form.education.length === 0 && (
            <p className="text-sm text-foreground-400 text-center py-4 bg-background-50 rounded-lg">
              Chưa có học vấn. Nhấn &quot;Thêm&quot; để bổ sung.
            </p>
          )}

          <div className="space-y-4">
            {form.education.map((edu, i) => (
              <div key={edu.id} className="p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-foreground-500">Học vấn #{i + 1}</span>
                  <button
                    onClick={() => updateField("education", form.education.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line"></i>
                    </div>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={edu.school}
                    onChange={(e) => updateEdu(i, "school", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Trường"
                  />
                  <input
                    type="text"
                    value={edu.degree}
                    onChange={(e) => updateEdu(i, "degree", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Bằng cấp (VD: Cử nhân)"
                  />
                  <input
                    type="text"
                    value={edu.field}
                    onChange={(e) => updateEdu(i, "field", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Chuyên ngành"
                  />
                  <input
                    type="text"
                    value={edu.graduation_year}
                    onChange={(e) => updateEdu(i, "graduation_year", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Năm tốt nghiệp"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => updateField("education", [...form.education, blankEdu()])}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Thêm
            </button>
          </div>
        </section>

        {/* Skills */}
        <section>
          <h4 className="font-heading font-semibold text-sm text-foreground-800 mb-4 flex items-center gap-2">
            <i className="ri-tools-line text-primary-500"></i>
            Kỹ năng
          </h4>
          <input
            type="text"
            value={form.skills}
            onChange={(e) => updateField("skills", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="VD: Hàn, Vận hành máy CNC, Lái xe nâng (phân cách bằng dấu phẩy)"
          />
        </section>

        {/* Certifications */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-sm text-foreground-800 flex items-center gap-2">
              <i className="ri-award-line text-primary-500"></i>
              Chứng chỉ
            </h4>
          </div>
          <div className="space-y-3">
            {form.certifications.map((cert, i) => (
              <div key={cert.id} className="flex items-center gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={cert.name}
                    onChange={(e) => updateCert(i, "name", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Tên chứng chỉ"
                  />
                  <input
                    type="text"
                    value={cert.issuer}
                    onChange={(e) => updateCert(i, "issuer", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Tổ chức cấp"
                  />
                  <input
                    type="text"
                    value={cert.year}
                    onChange={(e) => updateCert(i, "year", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="Năm"
                  />
                </div>
                <button
                  onClick={() => updateField("certifications", form.certifications.filter((_, j) => j !== i))}
                  className="text-red-400 hover:text-red-600 transition-colors cursor-pointer flex-shrink-0"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={() => updateField("certifications", [...form.certifications, blankCert()])}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Thêm
            </button>
          </div>
        </section>

        {/* Languages */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading font-semibold text-sm text-foreground-800 flex items-center gap-2">
              <i className="ri-global-line text-primary-500"></i>
              Ngôn ngữ
            </h4>
          </div>
          <div className="space-y-3">
            {form.languages.map((lang, i) => (
              <div key={lang.id} className="flex items-center gap-3">
                <input
                  type="text"
                  value={lang.name}
                  onChange={(e) => updateLang(i, "name", e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                  placeholder="Ngôn ngữ (VD: Tiếng Anh)"
                />
                <select
                  value={lang.level}
                  onChange={(e) => updateLang(i, "level", e.target.value)}
                  className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-all cursor-pointer"
                >
                  {languageLevels.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <button
                  onClick={() => updateField("languages", form.languages.filter((_, j) => j !== i))}
                  className="text-red-400 hover:text-red-600 transition-colors cursor-pointer flex-shrink-0"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={() => updateField("languages", [...form.languages, blankLang()])}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Thêm
            </button>
          </div>
        </section>

        {/* Save */}
        <div className="pt-6 border-t border-background-200/70 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <i className="ri-save-line text-base"></i>
                <span>Lưu CV</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line text-base"></i>
            </div>
            <span>Xem trước</span>
          </button>
        </div>
      </div>

      <CVPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        user={user}
        form={form}
      />
    </div>
  );
}